import { useEffect, useState } from "react";
import { z } from "zod";
import { FieldExtensionComponentProps, makeFieldSchema } from "@backstage/plugin-scaffolder-react";
import { ErrorPanel } from "@backstage/core-components";
import { extractValueFromFormData, substituteTemplate } from "../helpers";
import { FormControl, TextField, FormHelperText, Select, MenuItem } from "@material-ui/core";

const uiOptionsSchema = z.object({
    jmesPath: z.string().describe('The path to the value to extract.'),
    title: z.string().optional().describe('The title to display for the field'),
    placeholder: z.string().optional().describe('Placeholder text when source is empty (defaults to "N/A")'),
    description: z.string().optional().describe('The description to display for the field'),
    readonly: z.boolean().optional().describe('Whether the field is read-only').default(false),
});

export const DataSelectorFieldSchema = makeFieldSchema({
  output: zImpl => zImpl.string(),
  uiOptions: zImpl => zImpl.object({
    jmesPath: zImpl.string().describe('The path to the value to extract.'),
    title: zImpl.string().optional().describe('The title to display for the field'),
    placeholder: zImpl.string().optional().describe('Placeholder text when source is empty (defaults to "N/A")'),
    description: zImpl.string().optional().describe('The description to display for the field'),
    readonly: zImpl.boolean().optional().describe('Whether the field is read-only').default(false),
  }),
});

export const DataSelectorField = (props: FieldExtensionComponentProps<string, z.infer<typeof uiOptionsSchema>>) => {
    const { onChange, formData } = props;
    const { formData: allFormData } = props.formContext;

    const uiOptions = uiOptionsSchema.safeParse(props.uiSchema['ui:options']);
    if (!uiOptions.success) {
        return <ErrorPanel error={uiOptions.error} />;
    }

    const { jmesPath: sourcePath, title, placeholder = 'N/A', description, readonly } = uiOptions.data;
    const [extractedValue, setExtractedValue] = useState<string | undefined>('');
    const [isArray, setIsArray] = useState(false);
    const [arrayOptions, setArrayOptions] = useState<string[]>([]);

    // Substitute template variables in the sourcePath
    const { result: resolvedPath, isValid: pathValid, missingProperties: pathMissingProps } = substituteTemplate<string>(
        sourcePath,
        allFormData,
        (value) => String(value)
    );

    // Only extract value if the path is valid (all template variables resolved)
    const { value: sourceValue, exists: sourceExists } = pathValid
        ? extractValueFromFormData(resolvedPath, allFormData)
        : { value: null, exists: false };

    useEffect(() => {
        if (sourceExists && sourceValue) {
            const stringValue = String(sourceValue);
            setExtractedValue(stringValue);

            // Check if the extracted value is an array
            if (Array.isArray(sourceValue)) {
                setIsArray(true);
                setArrayOptions(sourceValue);
            } else {
                setIsArray(false);
                setArrayOptions([]);
                if (formData !== stringValue) {
                    onChange(stringValue);
                }
            }
        } else {
            setExtractedValue(undefined);
            onChange(undefined);
        }
    }, [sourceExists, sourceValue]);

    const fieldTitle = title || props.name;
    const displayValue = formData || extractedValue || placeholder;

    // Show error if path template has missing properties
    if (!pathValid && pathMissingProps.length > 0) {
        return (
            <FormControl
                margin="normal"
                required={props.required}
                error={true}
            >
                <TextField
                    label={fieldTitle}
                    value=""
                    disabled={true}
                    error={true}
                />
                <FormHelperText error>
                    Missing required properties for path: {pathMissingProps.join(', ')}
                </FormHelperText>
            </FormControl>
        );
    }


    return (
        <FormControl
            margin="normal"
            required={props.required}
            error={(props?.rawErrors?.length || 0) > 0 && !props.formData}
        >
            {isArray ? (
                <Select
                    label={fieldTitle}
                    value={displayValue}
                    onChange={(e) => onChange(e.target.value as string)}
                    disabled={readonly}
                >
                    {arrayOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            ) : (
                <TextField
                    label={fieldTitle}
                    value={displayValue}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    disabled={readonly}
                />
            )}
            <FormHelperText>
                {description}
            </FormHelperText>
        </FormControl>
    );
}
